import React, { useMemo, useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';

// Simple components for now
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import ScreenerPage from './pages/ScreenerPage';
import IndicatorTesting from './components/IndicatorTesting';

// Theme context for global toggle
export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'dark' });

function buildTheme(mode) {
  const common = {
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h4: { fontWeight: 700, fontSize: '2rem' },
      h5: { fontWeight: 600, fontSize: '1.5rem' },
      h6: { fontWeight: 600, fontSize: '1.25rem' },
      subtitle1: { fontWeight: 500 },
      body1: { fontSize: '0.95rem', lineHeight: 1.6 },
      body2: { fontSize: '0.85rem', lineHeight: 1.5 }
    },
    shape: { borderRadius: 12 },
    shadows: mode === 'dark' ? [
      'none', '0px 2px 4px rgba(0,0,0,0.3)', '0px 4px 8px rgba(0,0,0,0.25)',
      '0px 8px 16px rgba(0,0,0,0.2)', '0px 12px 24px rgba(0,0,0,0.15)',
      ...Array(20).fill('0px 16px 32px rgba(0,0,0,0.1)')
    ] : [
      'none', '0px 2px 4px rgba(0,0,0,0.08)', '0px 4px 8px rgba(0,0,0,0.06)',
      '0px 8px 16px rgba(0,0,0,0.04)', '0px 12px 24px rgba(0,0,0,0.03)',
      ...Array(20).fill('0px 16px 32px rgba(0,0,0,0.02)')
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: mode === 'dark' ? '#1a1d23' : '#f1f3f4' },
            '&::-webkit-scrollbar-thumb': {
              background: mode === 'dark' ? '#404148' : '#dadce0',
              borderRadius: '4px',
              '&:hover': { background: mode === 'dark' ? '#505258' : '#c4c7ca' }
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            padding: '8px 16px'
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: '0px 4px 12px rgba(0,0,0,0.15)' }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            fontWeight: 500
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            background: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            '& .MuiTableCell-head': {
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px'
            }
          }
        }
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: '10px !important',
            '&:before': { display: 'none' },
            marginBottom: '8px'
          }
        }
      }
    }
  };
  
  const palette = mode === 'dark' ? {
    mode,
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8'
    },
    secondary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706'
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669'
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706'
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626'
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b'
    },
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1'
    },
    divider: 'rgba(255,255,255,0.1)'
  } : {
    mode,
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8'
    },
    secondary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706'
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669'
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706'
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626'
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff'
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b'
    },
    divider: 'rgba(0,0,0,0.08)'
  };
  
  return responsiveFontSizes(createTheme({ palette, ...common }));
}

// Create React Query client
const queryClient = new QueryClient();

function App() {
  const [mode, setMode] = useState('dark');

  // Load persisted preference
  useEffect(() => {
    const saved = window.localStorage.getItem('flamingo-ui-mode');
    if (saved === 'light' || saved === 'dark') setMode(saved);
  }, []);

  const colorMode = useMemo(() => ({
    mode,
    toggleColorMode: () => {
      setMode(prev => {
        const next = prev === 'light' ? 'dark' : 'light';
        window.localStorage.setItem('flamingo-ui-mode', next);
        return next;
      });
    }
  }), [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <div className="App">
              <Navbar />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/screener" element={<ScreenerPage />} />
                <Route path="/indicator-testing" element={<IndicatorTesting />} />
              </Routes>
            </div>
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </ColorModeContext.Provider>
  );
}

export default App;